import { api, LightningElement } from "lwc";

import { assignedSlotNames } from "common/slot";
import { createTrack, TrackEvent } from "common/track";
import {
  hoursMinutesLabel,
  pointLabel,
  inProgressPointLabel,
} from "common/labels";

import { ResizeEvent, Size } from "tds/summary";
import { TruncateDisplayMode, TruncateType } from "tds/truncate";

import type { ContentSummaryConfig, ContentSummary } from "./types";
import { CtaEvent } from "tds/card";
export type { ContentSummaryConfig, ContentSummary };

const DEFAULT_CONFIG: ContentSummaryConfig = {
  hideDescription: false,
  hideMetaLeft: false,
  hideMetaRight: false,
  imageSize: "medium",
  imageVariant: "rounded",
  metaHtmlConfig: {
    ADD_TAGS: ["tds-star-rating", "tds-badge"],
    ADD_ATTR: ["target", "rating", "show-label", "variant"],
    FORBID_TAGS: ["style"],
  },
  titleMode: "anchor",
  variant: "brick",
};

export default class extends LightningElement {
  @api
  get config() {
    return this._config;
  }
  set config(config: ContentSummaryConfig) {
    this._config = {
      ...DEFAULT_CONFIG,
      ...config,
    };
  }
  private _config: ContentSummaryConfig = DEFAULT_CONFIG;

  @api summary: ContentSummary = null!;
  @api dynamicCta?: boolean;

  private track = createTrack<"tds-content-summary">(this);

  private size: Size = "default";
  private slots: string[] = [];
  private width = Number.MAX_SAFE_INTEGER;

  private get contentIconBackgroundColor() {
    switch (this.summary.type) {
      case "trail":
      case "trailmix":
        return this.summary.color ?? null;
      default:
        return null;
    }
  }

  private get description() {
    return this.config.hideDescription ? null : this.summary.description;
  }

  private get descriptionHtml() {
    return this.config.hideDescription ? null : this.summary.descriptionHtml;
  }

  private get imageVariant() {
    return this.summary.imageVariant || this.config.imageVariant;
  }

  private get actionHtml() {
    return this.config.hideAction ? null : this.summary.actionHtml;
  }

  private get metaRightCta() {
    return this.config.hideMetaRight ? null : this.summary.metaRightCta;
  }

  private get metaLeftHtml() {
    return this.config.hideMetaLeft ? null : this.summary.metaLeftHtml;
  }

  private get metaRightHtml() {
    return this.config.hideMetaRight ? null : this.summary.metaRightHtml;
  }

  private get preTitleTruncateLines() {
    return this.config.pretitleTruncateLines || 1;
  }

  private get titleTruncateLines() {
    return this.config.titleTruncateLines || 2;
  }

  private get descriptionTruncateLines(): number | null {
    if (this.config.descriptionTruncateLines) {
      return this.config.descriptionTruncateLines;
    }

    switch (this.summary.type) {
      case "post":
        return 7;
      case "question":
        return 5;
      case "group":
      case "topic":
        return 2;
      default:
        return 3;
    }
  }

  private get descriptionTruncateDisplayMode(): TruncateDisplayMode | null {
    if (this.config.descriptionTruncateDisplayMode) {
      return this.config.descriptionTruncateDisplayMode;
    }

    switch (this.summary.type) {
      case "post":
      case "question":
        return "fade";
      default:
        return null;
    }
  }

  private get descriptionTruncateType(): TruncateType | null {
    if (this.config.descriptionTruncateType) {
      return this.config.descriptionTruncateType;
    }

    switch (this.summary.type) {
      case "post":
      case "question":
        return "expand";
      default:
        return "hide";
    }
  }

  private get estimatedTime() {
    if (this.summary.estimatedTime) {
      return hoursMinutesLabel(this.summary.estimatedTime);
    }
    return undefined;
  }

  private get iconChecked() {
    return (
      typeof this.user.finishedAt === "string" || this.user.finishedAt?.value
    );
  }

  private get isProgressCompact() {
    return this.width < 350;
  }

  private get points() {
    const inProgress = this.user.pointsEarned;
    const points = inProgress ? this.user.pointsEarned : this.summary.points;

    if (inProgress) {
      return inProgressPointLabel(points);
    }

    return pointLabel(points);
  }

  private get pretitleLink() {
    return this.summary.pretitleLink || {};
  }

  private get showImage() {
    return this.summary.image && !this.slots.includes("image");
  }

  private get summaryImageAlt() {
    // For backward compatibility, falls back to summary title.
    return this.summary.imageAlt || this.summary.title;
  }

  private get showPoints() {
    return (
      !!this.summary.points &&
      !this.slots.includes("meta-left") &&
      !this.summary.metaLeft &&
      !this.summary.metaLeftHtml
    );
  }

  private get showProgress() {
    return (
      !this.slots.includes("meta-right") &&
      !this.summary.metaRight &&
      !this.summary.metaRightHtml &&
      !this.summary.metaRightCta
    );
  }

  private get user() {
    return this.summary.user || { finishedAt: null, pointsEarned: null };
  }

  private onNavigate() {
    this.dispatchEvent(
      new CustomEvent("navigate", {
        detail: this.summary,
      })
    );
  }

  private onDynamicCtaClick(e: CtaEvent) {
    this.dispatchEvent(
      new CustomEvent("dynamicctaclick", {
        detail: e.detail,
      })
    );
  }

  private onResize(e: CustomEvent<ResizeEvent>) {
    this.size = e.detail.size;
    this.width = e.detail.entry.contentRect.width;
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private onSlotChange() {
    this.slots = assignedSlotNames(this.template);
  }

  private handleSummaryTrack(e: TrackEvent<"tds-summary">) {
    this.track(e.detail.eventName, e.detail.payload);
  }
}
